import React, { useContext } from 'react';
import { Col, Descriptions, Row, Skeleton } from 'antd';

import SalesOrderDetailContext from '@totem/components/salesOrders/details/SalesOrderDetailContext';
import SalesOrderDetailLinesContainer from '@totem/components/salesOrders/details/SalesOrderDetailLinesContainer';
import SalesOrderDocumentsPanel from '@totem/components/salesOrders/details/SalesOrderDocumentsPanel';
import { Address } from '@totem/components/salesOrders/details/types';
import { salesOrdersContainerConfiguration } from '@totem/components/salesOrders/list/containerConfiguration';
import { SalesOrdersExtra } from '@totem/components/salesOrders/list/SalesOrdersLayout';
import SalesOrdersTable from '@totem/components/salesOrders/list/SalesOrdersTable';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import {
  DateStringToLocalDate,
  DateStringToUTCDate,
} from '@totem/utilities/dateUtilities';
import { isIBUser } from '@totem/utilities/security';

const formatAddress = (addr: Address) => {
  if (!addr) {
    return null;
  }
  const lines = [
    addr.addressee,
    addr.addr1,
    addr.addr2,
    addr.addr3,
    [addr.city, addr.state, addr.zip].filter(Boolean).join(', '),
    addr.country?.refName,
  ].filter(Boolean);

  return (
    <>
      {lines.map((line, idx) => (
        <div key={idx}>{line}</div>
      ))}
    </>
  );
};

type Props = {
  salesOrderNumber: string;
};

const SalesOrderDetailLayout = ({ salesOrderNumber }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const { loading, header } = useContext(SalesOrderDetailContext);

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8} style={{ display: 'flex' }}>
          <IBCard
            title="Customer"
            style={{ flex: 1 }}
            styles={{ body: { height: '100%' } }}
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Name">
                  {header?.customer?.companyName}
                </Descriptions.Item>
                <Descriptions.Item label="Entity ID">
                  {header?.customer?.entityId}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {header?.customer?.entityStatus?.refName}
                </Descriptions.Item>
                <Descriptions.Item label="Sales Rep">
                  {header?.customer?.salesRep?.refName}
                </Descriptions.Item>
              </Descriptions>
            )}
          </IBCard>
        </Col>
        <Col xs={24} md={8} style={{ display: 'flex' }}>
          <IBCard
            title="Billing Address"
            style={{ flex: 1 }}
            styles={{ body: { height: '100%' } }}
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              formatAddress(header?.billingAddress)
            )}
          </IBCard>
        </Col>
        <Col xs={24} md={8} style={{ display: 'flex' }}>
          <IBCard
            title="Shipping Address"
            style={{ flex: 1 }}
            styles={{ body: { height: '100%' } }}
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              formatAddress(header?.shippingAddress)
            )}
          </IBCard>
        </Col>
      </Row>

      <IBCard title="Sales Order Details" style={{ marginBottom: 16 }}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <Row>
            <Col span={8}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Transaction ID">
                  {header?.salesOrder?.tranId}
                </Descriptions.Item>
                <Descriptions.Item label="Department">
                  {header?.salesOrder?.department?.refName}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {header?.salesOrder?.status?.refName}
                </Descriptions.Item>
                {header?.salesOrder?.memo && (
                  <Descriptions.Item label="Memo" span={3}>
                    {header.salesOrder.memo}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Col>
            <Col span={8}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Start Date">
                  {DateStringToUTCDate(header?.salesOrder?.startDate)}
                </Descriptions.Item>
                <Descriptions.Item label="End Date">
                  {DateStringToUTCDate(header?.salesOrder?.endDate)}
                </Descriptions.Item>
                <Descriptions.Item label="Last Modified">
                  {DateStringToLocalDate(header?.salesOrder?.lastModifiedDate)}
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={8}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Terms">
                  {header?.salesOrder?.terms?.refName}
                </Descriptions.Item>

                <Descriptions.Item label="Currency">
                  {header?.salesOrder?.currency?.refName}
                </Descriptions.Item>
                <Descriptions.Item label="Exchange Rate">
                  {header?.salesOrder?.exchangeRate}
                </Descriptions.Item>

                <Descriptions.Item label="Subtotal">
                  {header?.salesOrder?.subtotal?.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  {header?.salesOrder?.total?.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        )}
      </IBCard>

      {isIBUser(userProfile) && (
        <SalesOrderDocumentsPanel salesOrderNumber={salesOrderNumber} />
      )}

      <InputContainer
        filterConfig={salesOrdersContainerConfiguration}
        defaultSort={{ field: 'nsSalesOrderLine.line', direction: 'ascend' }}
        componentKey={'salesOrder.lines'}
      >
        <SalesOrderDetailLinesContainer salesOrderNumber={salesOrderNumber}>
          <IBCard title="Lines" extra={<SalesOrdersExtra />}>
            <SalesOrdersTable />
          </IBCard>
        </SalesOrderDetailLinesContainer>
      </InputContainer>
    </>
  );
};

export default SalesOrderDetailLayout;
